import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Loader2, Plug, Plug2, PlugZap2, PlugZapIcon, X } from 'lucide-react';
import { Button } from '../common/Button';
import calendarService from '../../services/calendarService';
import { useToast } from '../common/Toast';

interface GoogleCalendarSettingsProps {
  onUpdate?: () => void;
}

export const GoogleCalendarSettings: React.FC<GoogleCalendarSettingsProps> = ({ onUpdate }) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const { showToast } = useToast();
  const [isGoogleCalendarEnabled, setIsGoogleCalendarEnabled] = useState<boolean>(false);

  useEffect(() => {
    const isEnabled = process.env.REACT_APP_GOOGLE_CALENDAR_ENABLED === 'true';
    setIsGoogleCalendarEnabled(isEnabled);
  }, []);

  // Check calendar connection status
  const checkCalendarStatus = async () => {
    setIsLoading(true);
    try {
      const connected = await calendarService.getCalendarStatus();
      setIsConnected(connected);
    } catch (error) {
      console.error('Error checking calendar status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkCalendarStatus();
  }, []);

  // Handle connect button click
  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const authUrl = await calendarService.getAuthUrl();
      if (authUrl) {
        // Instead of redirecting directly, open in a new tab
        window.open(authUrl, '_blank');
        showToast('Google Calendar authorization page opened in a new tab', 'info');
      } else {
        showToast('Failed to get authorization URL', 'error');
      }
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      showToast('Failed to connect to Google Calendar', 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle disconnect button click
  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const success = await calendarService.disconnectCalendar();
      if (success) {
        setIsConnected(false);
        showToast('Google Calendar disconnected successfully', 'success');
        if (onUpdate) onUpdate();
      } else {
        showToast('Failed to disconnect Google Calendar', 'error');
      }
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      showToast('Failed to disconnect Google Calendar', 'error');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div>
    {isGoogleCalendarEnabled ? (
    <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-4 md:p-5 mb-6 -mt-2">
      <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
        <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <img src="/google-calendar.png" alt="Google Calendar" className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Google Calendar</h3>
            </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
            Connect your Google Calendar to automatically track scheduled interviews to your calendar.
          </p>
          {isConnected && (
             <div className="flex items-center text-sm text-green-600 dark:text-green-400 mt-2">
               <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
               Connected
             </div>
          )}
        </div>

       {!isLoading ? (
        <>
      {isConnected ? (
          <Button
            variant="outline"
            size="sm"
            className="py-3 px-3 w-full md:w-auto"
            onClick={handleDisconnect}
            loading={isDisconnecting}
            disabled={isDisconnecting}
            leftIcon={<X size={16} />}
          >
            <span className="text-sm">{isDisconnecting ? 'Disconnecting...' : 'Disconnect Calendar'}</span>
          </Button>
      ) : (
        <Button
          variant="primary"
          size="sm"
          className="py-3 px-7 w-full md:w-auto"
          onClick={handleConnect}
          loading={isConnecting}
          disabled={isConnecting}
          rightIcon={<PlugZap2 size={16} />}
        >
          <span className="text-sm">{isConnecting ? 'Connecting...' : 'Connect Calendar'}</span>
        </Button>
      )}
      </>
      ) : (
        <div className="flex items-center justify-center mt-2">
          <Loader2 className="w-4 h-4 animate-spin text-text-primary-light dark:text-text-primary-dark" />
          <span className="text-sm ml-2">Checking calendar status...</span>
        </div>
      )
      }
      </div>
    </div>
    ) : (
      <div>
        { /* Nothing to show */ }
      </div>
    )}
    </div>
  );
}; 